import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayFill } from 'react-bootstrap-icons';
import VideoPlayer from '../shared/VideoPlayer';
import FlashSaleCountdown from './FlashSaleCountdown';
import { getRenewalDisclosure } from '../../data/flashSale/campaignHelpers';
import analyticsService from '../../services/analyticsService';

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function primaryCtaLabel(campaign, offer, busy) {
  if (busy) return 'Opening checkout…';
  if (!offer?.purchasable) return campaign.copy.expiredCta;
  if (offer.promoDisplay) {
    return `Get Pro · ${offer.promoDisplay}${offer.suffix}`;
  }
  return campaign.copy.primaryCta;
}

export function AnnouncementBar({ campaign, offer }) {
  if (!offer?.purchasable) return null;
  const parts = [campaign.copy.announcement];
  if (offer.discountPercent != null) parts.push(`${offer.discountPercent}% off`);
  if (offer.promoDisplay) parts.push(`${offer.promoDisplay}${offer.suffix}`);
  return (
    <div className="fs-announce" role="region" aria-label="Promotional announcement">
      <p className="fs-announce__text">{parts.join(' · ')}</p>
      <button
        type="button"
        className="fs-announce__link"
        onClick={() => scrollToId(campaign.cta.offerScrollTarget)}
      >
        See offer
      </button>
    </div>
  );
}

export function HeroSection({ campaign, offer, onPrimaryCta, onSecondaryCta, busy }) {
  const { copy, media } = campaign;
  return (
    <section className="fs-hero" aria-labelledby="fs-hero-heading">
      <div className="fs-hero__grid">
        <div className="fs-hero__copy">
          <p className="fs-eyebrow">{copy.eyebrow}</p>
          <h1 id="fs-hero-heading" className="fs-hero__title">
            {copy.headline}
          </h1>
          <p className="fs-hero__lead">
            {copy.supportingHeadlineEmphasis &&
            copy.supportingHeadline.includes(copy.supportingHeadlineEmphasis) ? (
              <>
                {copy.supportingHeadline.split(copy.supportingHeadlineEmphasis)[0]}
                <strong className="fs-hero__lead-em">
                  {copy.supportingHeadlineEmphasis}
                </strong>
                {copy.supportingHeadline.split(copy.supportingHeadlineEmphasis)[1]}
              </>
            ) : (
              copy.supportingHeadline
            )}
          </p>

          {offer.purchasable ? (
            <div className="fs-hero__offer" aria-label="Promotional offer">
              <span className="fs-hero__offer-label">{copy.promoCallout}</span>
              <div className="fs-hero__price-stack">
                <span className="fs-price-promo">
                  {offer.promoDisplay}
                  <span className="fs-price-suffix">{offer.suffix}</span>
                </span>
                <div className="fs-hero__price-meta">
                  <span className="fs-price-was">
                    <span className="visually-hidden">Was </span>
                    {offer.standardDisplay}
                    {offer.suffix}
                  </span>
                  {offer.discountPercent != null && (
                    <span className="fs-price-save">{offer.discountPercent}% off</span>
                  )}
                </div>
              </div>
              <FlashSaleCountdown
                endsAt={campaign.endsAt}
                className="fs-hero__countdown"
                label="Ends in"
              />
            </div>
          ) : (
            <div className="fs-hero__offer fs-hero__offer--inactive">
              <span className="fs-hero__offer-label">
                {offer.status === 'expired' ? 'Offer ended' : 'Offer unavailable'}
              </span>
              <p className="fs-hero__inactive-copy">
                {offer.status === 'expired'
                  ? copy.expiredSupporting
                  : 'This promotion isn’t active. Standard pricing is on the pricing page.'}
              </p>
            </div>
          )}

          <div className="fs-cta-row">
            <button
              type="button"
              className="fs-btn fs-btn--primary"
              onClick={onPrimaryCta}
              disabled={busy}
            >
              {primaryCtaLabel(campaign, offer, busy)}
            </button>
            <button
              type="button"
              className="fs-btn fs-btn--ghost"
              onClick={onSecondaryCta}
            >
              {copy.secondaryCta}
            </button>
          </div>
        </div>

        <figure className="fs-hero__visual">
          {media.heroVideo ? (
            <VideoPlayer
              src={media.heroVideo}
              poster={media.heroVideoPoster || media.heroImage}
              alt={media.heroImageAlt}
              title="PrecisionTrader product overview"
              videoLabel="flash-sale-hero-product-overview"
              analyticsContext={{ campaign_id: campaign.id, placement: 'hero' }}
              autoplay
              playWhenVisible
              loop
              muted
              controls={false}
              inline
              revealControlsOnHover={false}
            />
          ) : (
            <img
              src={media.heroImage}
              alt={media.heroImageAlt}
              width={960}
              height={640}
              decoding="async"
              fetchPriority="high"
            />
          )}
          <figcaption className="fs-hero__caption">
            Charts, rules, journaling, and risk tools in one workspace.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

export function ProblemSection() {
  const items = [
    {
      title: 'Revenge trading',
      body: 'One bad trade turns into three. Emotions override the plan.',
    },
    {
      title: 'Broken risk limits',
      body: 'Oversizing, skipping stops, or blowing the daily loss limit.',
    },
    {
      title: 'Same mistakes again',
      body: 'Without review, the same patterns keep costing you.',
    },
  ];

  return (
    <section className="fs-section fs-problem" aria-labelledby="fs-problem-heading">
      <h2 id="fs-problem-heading" className="fs-section__title">
        <span className="fs-section__title-line">You know your rules.</span>
        <span className="fs-section__title-line">Following them is the hard part.</span>
      </h2>
      <div className="fs-problem__grid">
        {items.map((c) => (
          <article key={c.title} className="fs-problem__item">
            <h3 className="fs-problem__item-title">{c.title}</h3>
            <p>{c.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function VslSection({ campaign, onCta, busy, offer }) {
  const { media } = campaign;
  const [showPlayer, setShowPlayer] = useState(false);
  const hasUrl = Boolean(media.vslUrl);

  const handlePlayIntent = () => {
    analyticsService.trackTradingEvent('flash_sale_vsl_play', {
      campaign_id: campaign.id,
      has_url: hasUrl,
    });
    if (hasUrl) setShowPlayer(true);
  };

  return (
    <section
      id={campaign.cta.secondaryScrollTarget}
      className="fs-section fs-vsl"
      aria-labelledby="fs-vsl-heading"
    >
      <h2 id="fs-vsl-heading" className="fs-section__title">
        See rules enforced in the platform.
      </h2>

      <div className="fs-vsl__frame">
        {hasUrl && showPlayer ? (
          <VideoPlayer
            src={media.vslUrl}
            poster={media.vslPoster}
            title={media.vslTitle}
            videoLabel="flash-sale-vsl"
            analyticsContext={{ campaign_id: campaign.id }}
            posterPreview={false}
            muted={false}
            autoplay
            controls
            inline
          />
        ) : (
          <button
            type="button"
            className="fs-vsl__placeholder"
            onClick={handlePlayIntent}
            aria-label={hasUrl ? `Play ${media.vslTitle}` : 'Product walkthrough video coming soon'}
          >
            <img src={media.vslPoster} alt="" className="fs-vsl__poster" loading="lazy" />
            <span className="fs-vsl__play" aria-hidden>
              <PlayFill size={36} />
            </span>
            {!hasUrl && (
              <span className="fs-vsl__soon">Product walkthrough video coming soon</span>
            )}
          </button>
        )}
      </div>

      <div className="fs-cta-row fs-cta-row--center">
        <button
          type="button"
          className="fs-btn fs-btn--primary"
          onClick={onCta}
          disabled={busy}
        >
          {primaryCtaLabel(campaign, offer, busy)}
        </button>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  const steps = [
    {
      title: 'Connect a broker',
      body: 'Monitor and review trades from a supported account. Monitoring alone does not block orders placed elsewhere.',
    },
    {
      title: 'Set your rules',
      body: 'Risk limits and behavioral rules — track, alert, or enforce where supported.',
    },
    {
      title: 'Trade with structure',
      body: 'Use PrecisionTrader’s live path for enforced rules, risk sizing, and review.',
    },
  ];

  return (
    <section className="fs-section fs-how" aria-labelledby="fs-how-heading">
      <h2 id="fs-how-heading" className="fs-section__title">
        How it works
      </h2>
      <ol className="fs-how__list fs-how__list--compact">
        {steps.map((s, i) => (
          <li key={s.title} className="fs-how__step fs-how__step--compact">
            <span className="fs-step-num">0{i + 1}</span>
            <div className="fs-how__copy">
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="fs-section__note">
        Live trading and hard enforcement are supported for TradeStation and Tastytrade only right
        now. Other brokers are being onboarded —{' '}
        <Link to="/brokers">learn more about our supported brokers</Link>.
      </p>
    </section>
  );
}

export function FeaturesSection() {
  const features = [
    {
      title: 'Real-time enforcement',
      subtitle: 'Hard blocks when you trade live here.',
      body: 'In Enforce mode, rule-breaking orders are blocked on supported PrecisionTrader live workflows.',
    },
    {
      title: 'Automatic position sizing',
      subtitle: 'Risk first, size second.',
      body: 'Set risk per trade. Entry and stop distance calculate quantity automatically.',
    },
    {
      title: 'Automatic journaling',
      subtitle: 'Less busywork, more review.',
      body: 'Trades sync from your broker so journaling stays a habit instead of homework.',
    },
    {
      title: 'Behavioral analytics',
      subtitle: 'See the patterns costing you.',
      body: 'Review rule violations and trading behavior so the same mistakes don’t keep repeating.',
    },
  ];

  return (
    <section className="fs-section fs-features" aria-labelledby="fs-features-heading">
      <h2 id="fs-features-heading" className="fs-section__title">
        Built for executing the plan
      </h2>
      <div className="fs-features__grid fs-features__grid--cards">
        {features.map((f) => (
          <article key={f.title} className="fs-feature-card">
            <h3 className="fs-feature-card__title">{f.title}</h3>
            <p className="fs-feature-card__subtitle">{f.subtitle}</p>
            <p className="fs-feature-card__body">{f.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function DifferentiationSection() {
  return (
    <section className="fs-section fs-diff" aria-labelledby="fs-diff-heading">
      <h2 id="fs-diff-heading" className="fs-section__title">
        Not just another journal
      </h2>
      <div className="fs-diff__grid">
        <div className="fs-diff__col">
          <h3>Typical journal</h3>
          <ul>
            <li>Documents trades after the fact</li>
            <li>You still have to spot the patterns</li>
            <li>Doesn’t stop a live order</li>
          </ul>
        </div>
        <div className="fs-diff__col fs-diff__col--accent">
          <h3>PrecisionTrader</h3>
          <ul>
            <li>Stops you from placing trades that break your rules</li>
            <li>Tracks violations and trading behavior</li>
            <li>Trading + risk tools + analytics in one place</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

export function OfferSection({
  campaign,
  offer,
  onCta,
  busy,
  onExpire,
  offerSectionRef,
}) {
  const disclosure = getRenewalDisclosure(offer);
  const observed = useRef(false);

  useEffect(() => {
    const node = offerSectionRef?.current;
    if (!node || observed.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !observed.current) {
          observed.current = true;
          analyticsService.trackTradingEvent('flash_sale_pricing_view', {
            campaign_id: campaign.id,
            status: offer.status,
          });
        }
      },
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [campaign.id, offer.status, offerSectionRef]);

  return (
    <section
      id={campaign.cta.offerScrollTarget}
      ref={offerSectionRef}
      className="fs-section fs-section--band fs-offer"
      aria-labelledby="fs-offer-heading"
    >
      <div className="fs-section__inner">
        <h2 id="fs-offer-heading" className="fs-section__title">
          {campaign.copy.offerHeadline}
        </h2>
        <p className="fs-section__sub">{campaign.copy.offerSupporting}</p>

        {offer.purchasable ? (
          <div className="fs-offer-board">
            <span className="fs-offer-board__plan">
              {offer.plan.name} · billed {offer.billingInterval === 'annual' ? 'yearly' : 'monthly'}
            </span>
            <div className="fs-offer-board__price-stack">
              <span className="fs-price-promo">
                {offer.promoDisplay}
                <span className="fs-price-suffix">{offer.suffix}</span>
              </span>
              <div className="fs-offer-board__price-meta">
                <span className="fs-price-was">
                  <span className="visually-hidden">Was </span>
                  {offer.standardDisplay}
                  {offer.suffix}
                </span>
                {offer.discountPercent != null && (
                  <span className="fs-price-save">{offer.discountPercent}% off</span>
                )}
              </div>
            </div>
            {disclosure && <p className="fs-offer-board__renewal">{disclosure}</p>}
            <FlashSaleCountdown
              endsAt={campaign.endsAt}
              onExpire={onExpire}
              className="fs-offer-board__countdown"
              label="Ends in"
            />
            <button
              type="button"
              className="fs-btn fs-btn--primary fs-btn--block"
              onClick={onCta}
              disabled={busy}
            >
              {primaryCtaLabel(campaign, offer, busy)}
            </button>
          </div>
        ) : (
          <div className="fs-offer-board fs-offer-board--expired">
            <h3>{campaign.copy.expiredHeadline}</h3>
            <p>{campaign.copy.expiredSupporting}</p>
            <p className="fs-offer-board__standard">
              {offer.plan.displayName}: {offer.standardDisplay}
              {offer.suffix}
            </p>
            <Link to={campaign.cta.expiredFallbackPath} className="fs-btn fs-btn--primary">
              {campaign.copy.expiredCta}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export function GuaranteeSection({ campaign }) {
  if (!campaign.copy.guaranteeEnabled) return null;

  return (
    <section className="fs-section fs-guarantee" aria-labelledby="fs-guarantee-heading">
      <h2 id="fs-guarantee-heading" className="fs-section__title">
        {campaign.copy.guaranteeHeadline}
      </h2>
      <p className="fs-section__sub">{campaign.copy.guaranteeBody}</p>
      <p className="fs-section__note">{campaign.copy.guaranteeDetails}</p>
    </section>
  );
}

export function FaqSection({ campaign, offer }) {
  const renewalNote =
    offer.discountScope === 'recurring'
      ? `Yes — ${offer.promoDisplay}${offer.suffix} while the promo lasts, until you cancel.`
      : offer.discountScope === 'first_period'
        ? `No — first period at ${offer.promoDisplay}${offer.suffix}, then ${offer.renewalDisplay}${offer.suffix} unless you cancel.`
        : `No — ${offer.promoDisplay}${offer.suffix} for the first year, then ${offer.renewalDisplay}${offer.suffix} unless you cancel.`;

  const items = [
    {
      q: 'What does PrecisionTrader do?',
      a: 'You set trading rules and risk limits, then monitor and journal from a connected broker. In Enforce mode on supported live paths, rule-breaking orders are blocked. Not a signal service — and it doesn’t guarantee profits.',
    },
    {
      q: 'How does enforcement work?',
      a: 'Track records violations (including on a monitored account). Enforce blocks new orders server-side when you trade through PrecisionTrader’s supported live path. You can still manage open positions. Orders placed in another platform are not blocked by monitoring alone.',
    },
    {
      q: 'Do I have to trade inside PrecisionTrader?',
      a: 'No. Monitor, journal, and review without placing orders here. Hard blocks apply on PrecisionTrader’s live trading path.',
    },
    {
      q: 'Which brokers are supported?',
      a: 'Live trading and hard enforcement are supported for TradeStation and Tastytrade only right now. Other brokers are being onboarded.',
      link: { href: '/brokers', text: 'Learn more about our supported brokers' },
    },
    {
      q: 'What happens when the sale ends?',
      a: 'This promo price goes away. Standard pricing stays on the pricing page.',
    },
    {
      q: 'Is the sale price recurring?',
      a: renewalNote,
    },
    {
      q: 'Can I cancel?',
      a: 'Yes — anytime in account settings, no cancellation fees.',
    },
    {
      q: 'What happens after I subscribe?',
      a: 'Checkout completes → you’re on Trade. Connect a broker, set rules, and use alerts or live execution where your broker supports it.',
    },
  ];

  if (campaign.copy.guaranteeEnabled) {
    items.splice(7, 0, {
      q: 'How does the refund guarantee work?',
      a: campaign.copy.guaranteeDetails,
    });
  }

  return (
    <section className="fs-section fs-faq" aria-labelledby="fs-faq-heading">
      <h2 id="fs-faq-heading" className="fs-section__title">
        FAQ
      </h2>
      <div className="fs-faq__list">
        {items.map((item) => (
          <details key={item.q} className="fs-faq__item">
            <summary>{item.q}</summary>
            <div className="fs-faq__answer">
              <p>{item.a}</p>
              {item.link && (
                <p>
                  <Link to={item.link.href}>{item.link.text}</Link>
                </p>
              )}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

